import React, { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { schoolClassSortOrder } from '../services/TimetableService';
import './AdminEventsView.css';
import './AdminMoreDetailPages.css';
import StatCards from './StatCards';
import { scopeForAdmin, adminSchoolIds } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';

function AdminEventsView({ adminData }) {
  const [events, setEvents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  const [newEvent, setNewEvent] = useState({
    name: '',
    eventType: 'Marathon',
    date: '',
    location: { latitude: 0, longitude: 0, address: '' },
    description: '',
    participatingClassIds: [],
    imageURL: ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const eventTypes = ['Marathon', 'Walkathon', 'Fun Run', 'Track and Field', 'Field Trip', 'Assembly', 'Sporting Event', 'Walk', 'Other'];

  // School-scoped cache key builder — shared with AdminAnalyticsView so tabs
  // reuse each other's fetched collections.
  const ck = (p) => `${adminSchoolIds(adminData).join('|') || 'all'}:${p}`;

  const loadEvents = useCallback(async () => {
    try {
      let eventsData = await dataCacheService.getDocs(ck('events'), collection(db, 'events'));
      eventsData = scopeForAdmin(eventsData, adminData);
      setEvents(eventsData);
    } catch (error) {
      console.error('🔥 AdminEvents: Error loading events:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData?.schoolId]);

  const loadClasses = useCallback(async () => {
    try {
      let classesData = await dataCacheService.getDocs(ck('classes'), collection(db, 'classes'));
      classesData = scopeForAdmin(classesData, adminData);
      classesData.sort(schoolClassSortOrder);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 AdminEvents: Error loading classes:', error);
    }
  }, [adminData?.schoolId]);

  useEffect(() => {
    console.log('🔥 AdminEvents: Component mounted, starting data load');
    loadEvents();
    loadClasses();
  }, [loadEvents, loadClasses]);

  const handleAddEvent = async (e) => {
    e.preventDefault();
    try {
      let imageURL = newEvent.imageURL;
      
      if (selectedImage) {
        setIsUploadingImage(true);
        const imageRef = ref(storage, `event_images/${Date.now()}_${selectedImage.name}`);
        await uploadBytes(imageRef, selectedImage);
        imageURL = await getDownloadURL(imageRef);
        setIsUploadingImage(false);
      }

      await addDoc(collection(db, 'events'), {
        ...newEvent,
        imageURL,
        date: new Date(newEvent.date).toISOString(),
        isActive: true,
        status: 'upcoming',
        createdAt: new Date().toISOString(),
        schoolId: adminData?.schoolId || null
      });
      dataCacheService.invalidatePath('events');
      setShowAddForm(false);
      setNewEvent({
        name: '',
        eventType: 'Marathon',
        date: '',
        location: { latitude: 0, longitude: 0, address: '' },
        description: '',
        participatingClassIds: [],
        imageURL: ''
      });
      setSelectedImage(null);
      setImagePreview(null);
      loadEvents();
    } catch (error) {
      console.error('Error adding event:', error);
      alert('Failed to add event');
      setIsUploadingImage(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event?')) return;
    try {
      await updateDoc(doc(db, 'events', eventId), { isActive: false });
      dataCacheService.invalidatePath('events');
      loadEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event');
    }
  };

  const handleClassToggle = (classId) => {
    setNewEvent(prev => {
      const selected = prev.participatingClassIds.includes(classId);
      if (selected) {
        return { ...prev, participatingClassIds: prev.participatingClassIds.filter(id => id !== classId) };
      } else {
        return { ...prev, participatingClassIds: [...prev.participatingClassIds, classId] };
      }
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const getEventIcon = (eventType) => {
    const icons = {
      'Marathon': '🏃',
      'Walkathon': '🚶',
      'Fun Run': '🏃',
      'Track and Field': '🏟️',
      'Field Trip': '🚌',
      'Assembly': '👥',
      'Sporting Event': '⚽',
      'Walk': '🚶',
      'Other': '📅'
    };
    return icons[eventType] || '📅';
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString();
    return new Date(dateValue).toLocaleDateString();
  };

  if (loading) {
    console.log('🔥 AdminEvents: Rendering loading state');
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // === Full-screen event detail page ===
  if (selectedEvent) {
    const ev = selectedEvent;
    const locationObj = typeof ev.location === 'object' ? ev.location : null;
    const locationText = locationObj ? (locationObj.address || '') : (ev.location || '');
    const participating = (ev.participatingClassIds || [])
      .map(id => classes.find(c => c.id === id)?.name || id);
    const hasCoords = locationObj && Number.isFinite(locationObj.latitude) && Number.isFinite(locationObj.longitude) && (locationObj.latitude !== 0 || locationObj.longitude !== 0);
    const eventDate = ev.date ? (ev.date.toDate ? ev.date.toDate() : new Date(ev.date)) : null;
    console.log('🔥 AdminEvents: Showing detail for event', ev.id, ev);
    return (
      <div className="admin-events-view detail-page">
        <button className="detail-back-button" onClick={() => setSelectedEvent(null)}>
          ◄ Back to Events
        </button>
        <div className="detail-card">
          {ev.imageURL && <img src={ev.imageURL} alt={ev.name} className="detail-hero" />}
          <div className="detail-header">
            <div>
              <h2>{getEventIcon(ev.eventType)} {ev.name}</h2>
              <p className="detail-subtitle">{ev.eventType || 'Event'}</p>
            </div>
            <span className="detail-status-badge" style={{
              background: ev.status === 'upcoming' ? '#dcfce7' : ev.status === 'completed' ? '#d1fae5' : '#f3f4f6',
              color: ev.status === 'upcoming' ? '#15803d' : ev.status === 'completed' ? '#065f46' : '#374151'
            }}>
              {ev.status || 'unknown'}
            </span>
          </div>

          <div className="detail-grid">
            <div className="detail-field">
              <span className="detail-label">Date</span>
              <span className="detail-value">{eventDate ? eventDate.toLocaleDateString() : 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Time</span>
              <span className="detail-value">{ev.time || (eventDate ? eventDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A')}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Event Type</span>
              <span className="detail-value">{ev.eventType || 'N/A'}</span>
            </div>
            {locationText && (
              <div className="detail-field">
                <span className="detail-label">Location</span>
                <span className="detail-value">📍 {locationText}</span>
              </div>
            )}
            {hasCoords && (
              <div className="detail-field">
                <span className="detail-label">Coordinates</span>
                <span className="detail-value">{locationObj.latitude.toFixed(5)}, {locationObj.longitude.toFixed(5)}</span>
              </div>
            )}
            {ev.audience && (
              <div className="detail-field">
                <span className="detail-label">Audience</span>
                <span className="detail-value">{ev.audience}</span>
              </div>
            )}
            <div className="detail-field">
              <span className="detail-label">Created</span>
              <span className="detail-value">{formatDate(ev.createdAt)}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Active</span>
              <span className="detail-value">{ev.isActive !== false ? 'Yes' : 'No'}</span>
            </div>
          </div>

          {ev.description && (
            <div className="detail-section">
              <h4>Description</h4>
              <p className="detail-text">{ev.description}</p>
            </div>
          )}

          <div className="detail-section">
            <h4>Participating Classes</h4>
            {participating.length > 0 ? (
              <div className="detail-chip-list">
                {participating.map((name, i) => (
                  <span key={i} className="detail-chip">{name}</span>
                ))}
              </div>
            ) : (
              <p className="detail-empty-text">All classes / no specific classes selected</p>
            )}
          </div>

          {(ev.attendeeCount != null || (ev.attendees && ev.attendees.length > 0) || (ev.responses && Object.keys(ev.responses).length > 0)) && (
            <div className="detail-section">
              <h4>Attendees / Responses</h4>
              <div className="detail-grid">
                {ev.attendeeCount != null && (
                  <div className="detail-field">
                    <span className="detail-label">Attendee Count</span>
                    <span className="detail-value">{ev.attendeeCount}</span>
                  </div>
                )}
                {ev.responses && (
                  <div className="detail-field">
                    <span className="detail-label">Responses</span>
                    <span className="detail-value">{Object.keys(ev.responses).length}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="detail-actions">
            <button className="detail-btn-danger" onClick={async () => { await handleDeleteEvent(ev.id); setSelectedEvent(null); }}>
              🗑️ Delete Event
            </button>
          </div>
        </div>
      </div>
    );
  }

  console.log('🔥 AdminEvents: Rendering main view, events:', events.length, 'classes:', classes.length);

  return (
    <div className="admin-events-view">
      <div className="view-header">
        <h2>Events</h2>
        <button className="add-button" onClick={() => setShowAddForm(true)}>
          + Add Event
        </button>
      </div>
      <StatCards items={[['Events',events.length],['Upcoming',events.filter(e => new Date(e.date) > new Date()).length],['Past',events.filter(e => new Date(e.date) < new Date() && new Date(e.date).toDateString() !== new Date().toDateString()).length],['Today',events.filter(e => new Date(e.date).toDateString() === new Date().toDateString()).length],['Classes',classes.length]]} />

      <p className="count-text">Events: {events.length}</p>

      {showAddForm && (
        <div className="add-event-form">
          <h3>Create New Event</h3>
          <form onSubmit={handleAddEvent}>
            <div className="form-group">
              <label>Event Name *</label>
              <input
                type="text"
                value={newEvent.name}
                onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Event Type *</label>
                <select
                  value={newEvent.eventType}
                  onChange={(e) => setNewEvent({ ...newEvent, eventType: e.target.value })}
                  required
                >
                  {eventTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Location Address</label>
              <input
                type="text"
                value={newEvent.location?.address || ''}
                onChange={(e) => setNewEvent({ ...newEvent, location: { ...newEvent.location, address: e.target.value } })}
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="form-group">
              <label>Event Image</label>
              <div className="image-upload-container">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  id="event-image-input"
                  style={{ display: 'none' }}
                />
                <label htmlFor="event-image-input" className="image-upload-button">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="image-preview" />
                  ) : (
                    <div className="image-placeholder">
                      <span>📷</span>
                      <span>Tap to add event image</span>
                    </div>
                  )}
                </label>
                {selectedImage && (
                  <button
                    type="button"
                    className="remove-image-button"
                    onClick={handleRemoveImage}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Participating Classes</label>
              <div className="class-checkboxes">
                {classes.map(cls => (
                  <label key={cls.id} className="class-checkbox">
                    <input
                      type="checkbox"
                      checked={newEvent.participatingClassIds.includes(cls.id)}
                      onChange={() => handleClassToggle(cls.id)}
                    />
                    <span>{cls.name} ({cls.grade})</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit" disabled={isUploadingImage}>
                {isUploadingImage ? 'Uploading Image...' : 'Create Event'}
              </button>
            </div>
          </form>
        </div>
      )}

      {events.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📅</div>
          <h3>No Events</h3>
          <p>Create your first event to get started</p>
        </div>
      ) : (
        <div className="events-list">
          {events.map(event => (
            <div key={event.id} className="event-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedEvent(event)}>
              {event.imageURL ? (
                <div className="event-image">
                  <img src={event.imageURL} alt={event.name} />
                </div>
              ) : (
                <div className="event-icon">{getEventIcon(event.eventType)}</div>
              )}
              <div className="event-info">
                <h4>{event.name}</h4>
                <p>{event.eventType}</p>
                <p>Date: {formatDate(event.date)}</p>
                {event.location && typeof event.location === 'object' && event.location.address && <p>📍 {event.location.address}</p>}
                {event.location && typeof event.location === 'string' && <p>📍 {event.location}</p>}
                {event.description && <p>{event.description}</p>}
              </div>
              <div className="event-status">
                <span className={`status-badge ${event.status}`}>{event.status}</span>
              </div>
              <button
                className="delete-button"
                onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event.id); }}
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminEventsView;
